/**
 * Currency menu (component for switching currencies)
 * Uses state.inchcoins
 */
import { useEffect, useState } from 'react';
import { AutoSizer, List } from 'react-virtualized';
import { IToken, ITokenCoin, Network } from '@kattana-io/common';
import BigNumber from 'bignumber.js';
import { useNetwork } from 'wagmi';
import SmallCurrencyIcon from '../../common/SmallCurrencyIcon';
import Text from '../../../../atoms/Text';
import Image from '../../../../atoms/Image';
import FeaturedToken from './FeaturedToken';
import { IconsMapping } from '../../../ConnectedWallet/NetworkBaseCoin';
import big_arrow_left from '../../../../assets/images/arrow_left.svg';
import {
  useGetTokensByNetwork,
  useGetFeaturedTokensByNetwork,
} from '../../hooks';
import { NativeCoin } from '../../helpers/nativeCoins';
import { ICoin } from '../../reducers/tradingReducer';

import './styles.scss';

export interface ICurrencyMenu {
  walletCoinsOnly: boolean;
  selected: ICoin;
  restricted: ICoin;
  onSelect: (token: ICoin) => void;
  balances: any;
  balancesMapping: any;
  onCloseMenu: () => void;
}

const LIST_PADDING = 120;

const tokenToCoin = (token: IToken): ICoin => {
  return {
    name: token.symbol || token.name,
    decimals: token.decimals,
    amount: new BigNumber(0.0),
    address: token.address,
    symbol: token.name,
  } as ICoin;
};

const useJoinBalances = (tkns: any[], balancesMapping: any) => {
  const [tokens, setTokens] = useState<ICoin[]>([]);

  useEffect(() => {
    if (tkns) {
      const mapping: any = Object.fromEntries(
        Object.entries(balancesMapping).map(([k, v]) => [k.toLowerCase(), v])
      );
      const joinedTokens: IterableIterator<ICoin> = tkns
        .reduce((acc: Map<string, ICoin>, t: IToken) => {
          const c = tokenToCoin(t);
          if (mapping[c.address.toLowerCase()]) {
            c.balance = mapping[c.address.toLowerCase()];
          }
          acc.set(c.address.toLowerCase(), c);

          return acc;
        }, new Map())
        .values();
      const sorted = Array.from(joinedTokens).sort((a, b) => {
        const aHas = typeof a.balance !== 'undefined';
        const bHas = typeof b.balance !== 'undefined';
        // @ts-ignore
        return bHas - aHas || (aHas === true && b.balance - a.balance) || 0;
      });
      setTokens(sorted);
    } else {
      setTokens([]);
    }
  }, [tkns, balancesMapping]);

  return tokens;
};

/**
 * Display 8 symbols or less of balance number
 * @param input - number of undefined
 * @returns stringified balance
 */
const balanceFormatter = (input?: number): string => {
  if (input) {
    const sn = String(input);
    return sn.length > 8 ? sn.substring(0, 8) : sn;
  } else {
    return '0';
  }
};

const CurrencyMenu = ({
  // inchcoins,
  walletCoinsOnly,
  selected,
  restricted,
  balances,
  balancesMapping,
  onSelect,
  onCloseMenu,
}: ICurrencyMenu) => {
  const { chain } = useNetwork();
  const [search, setSearch] = useState('');
  const [featTokens, setFeatTokens] = useState<ITokenCoin[]>([]);

  const network = Network.fromChainId(chain?.id);
  const tkns = useGetTokensByNetwork(network?.getSymbol() || 'ETH', search);
  const featuredTokens = useGetFeaturedTokensByNetwork(
    network?.getSymbol() || 'ETH',
    search
  );

  useEffect(() => {
    const nativeCoin = NativeCoin(chain?.id || 1, new BigNumber(0)) as ICoin;
    const mapping = IconsMapping[nativeCoin.name];

    const nativeTokenCoin = {
      ...nativeCoin,
      coin: {
        logo_url: mapping,
      } as any,
    } as any;

    featuredTokens.unshift(nativeTokenCoin);

    // this trick with state is to prevent jump's on render
    setFeatTokens(featuredTokens);
  }, [featuredTokens]);

  const tokens = useJoinBalances(tkns, balancesMapping);

  const rowRenderer = ({
    key, // Unique key within array of rows
    index, // Index of row within collection
    isScrolling, // The List is currently being scrolled
    isVisible, // This row is visible within the List (eg it is not an overscanned row)
    style, // Style object to be applied to row (to position it)
  }: any) => {
    const item = tokens[index];
    const isActive = item.address === selected.address;
    const isRestricted = item.address === restricted.address;
    const rowClassList = `currency_menu__item ${
      isActive ? 'currency_menu__item--active' : ''
    } ${isRestricted ? 'currency_menu__item--disabled' : ''}`;

    return (
      <div
        key={key}
        style={style}
        onClick={isRestricted ? undefined : () => onSelect(item)}
      >
        <div className={rowClassList}>
          <div className='currency_menu__item__icon'>
            <SmallCurrencyIcon
              network={network as any}
              className='img'
              currency={item?.symbol as string}
              contract={item?.address}
              variant='m'
            />
          </div>
          <div className='currency_menu__item__body'>
            <Text className='name' size={14}>
              {item?.name}
            </Text>
            <Text className='slug' size={10}>
              {item?.symbol}
            </Text>
          </div>
          <div className='currency_menu__item__balance'>
            <Text className='balance' size={14}>
              {balanceFormatter(item.balance)}
            </Text>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className='currency_menu'>
      <div className='header'>
        <div className='close__btn' onClick={onCloseMenu}>
          <Image size='s' src={big_arrow_left} />
        </div>
        <Text size={14}>Select currency</Text>
        <div className='placeholder' />
      </div>
      <div className='currency_menu--container'>
        <div className='search__wrapper'>
          <input
            type='search'
            className='search__wrapper__input text--14'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='Search...'
            autoComplete='off'
          />
        </div>
        <div className='featured__wrapper'>
          {featTokens.map((token) => {
            return (
              <FeaturedToken key={token.id} token={token} onClick={onSelect} />
            );
          })}
        </div>
        <div className='row table_header'>
          <Text size={10}>TOKEN NAME</Text>
          <Text size={10}>BALANCE</Text>
        </div>
        <AutoSizer>
          {({ width, height }) => (
            <List
              width={width - 1}
              height={height - LIST_PADDING}
              rowCount={tokens?.length || 0}
              rowHeight={40}
              style={{ marginTop: '8px' }}
              rowRenderer={rowRenderer}
            />
          )}
        </AutoSizer>
      </div>
      {/* <div className="currencyMenu__backdrop" onClick={onCloseMenu}/> */}
    </div>
  );
};

export default CurrencyMenu;
